import { useState } from "react"
import { Button } from "@/components/ui/button"
import { CheckCircle2, ArrowRight, Code, Layers, RefreshCw } from "lucide-react"
import { DemoBookingModal } from "./demo-booking-modal"

const differentiators = [
  {
    icon: Code,
    title: "100% No-Code Solution",
    description: "Build complex data integrations visually without writing a single line of code. Our low-code platform makes healthcare data accessible to everyone.",
  },
  {
    icon: Layers,
    title: "Fully Managed E/iPaaS",
    description: "End-to-end monitoring, error handling, and support. We handle the infrastructure so you can focus on your core business.",
  },
  {
    icon: RefreshCw,
    title: "Flexible Deployment",
    description: "Deploy on-premises, in your cloud, or use our managed cloud. Choose the architecture that fits your security requirements.",
  },
]

export function VorroDifferenceSection() {
  const [isDemoOpen, setIsDemoOpen] = useState(false)

  return (
    <section id="why-vorro" className="py-20 lg:py-24 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-4">
          <p className="text-[#9e3667] font-semibold text-sm uppercase tracking-wide">The Vorro Advantage</p>
        </div>
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold tracking-tight text-[#2d2d3a] sm:text-4xl">
            Move Beyond Integration{" "}
            <span className="bg-[linear-gradient(135deg,#a63083,#bf406a)] bg-clip-text text-transparent">Complexity</span>
          </h2>
          <p className="mt-4 text-lg text-[#6b6b7b] max-w-3xl mx-auto">
            Traditional middleware, manual data munging, legacy ETL, and custom developer integrations. Vorro is replacing it all.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3 mb-16">
          {differentiators.map((item) => (
            <div 
              key={item.title} 
              className="bg-white rounded-xl p-6 border border-[#e8e8ec] hover:border-[#9e3667]/40 hover:shadow-lg transition-all"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#faf5f7] mb-4">
                <item.icon className="h-6 w-6 text-[#9e3667]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2d2d3a] mb-2">{item.title}</h3>
              <p className="text-sm text-[#6b6b7b] leading-relaxed mb-4">
                {item.description}
              </p>
              <button 
                onClick={() => setIsDemoOpen(true)}
                className="inline-flex items-center gap-1 text-sm font-medium text-[#9e3667] hover:text-[#843054] transition-colors"
              >
                Talk to an Expert <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>

        {/* The Vorro Difference callout */}
        <div className="bg-[#faf5f7] rounded-2xl p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-[#2d2d3a] mb-4">The Vorro Difference</h3>
              <p className="text-[#6b6b7b] leading-relaxed mb-6">
                We know that in healthcare, data format is the biggest hurdle. Vorro connects your business by adapting to your current operations. We accept your data in its existing format, then handle the formatting and cleanup for its intended destination.
              </p>
              <p className="text-[#6b6b7b] leading-relaxed mb-6">
                Plus, you aren't doing this alone. We monitor and support the implementation 24/7 through our managed services. Essentially, with Vorro, we take care of it for you.
              </p>
              <Button 
                onClick={() => setIsDemoOpen(true)}
                className="bg-[linear-gradient(135deg,#a63083,#bf406a)] hover:opacity-90 text-white font-medium"
              >
                Learn More About Our Approach
              </Button>
            </div>
            <div className="space-y-4">
              {[
                { title: "Accept any data format", desc: "HL7, FHIR, X12, or custom formats" },
                { title: "Automated formatting & cleanup", desc: "Data arrives clean and validated" },
                { title: "24/7 managed services", desc: "Round-the-clock monitoring and support" },
                { title: "Enterprise-grade security", desc: "HIPAA compliant with full audit trails" },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[linear-gradient(135deg,#a63083,#bf406a)] flex-shrink-0 mt-0.5">
                    <CheckCircle2 className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#2d2d3a]">{item.title}</p>
                    <p className="text-sm text-[#6b6b7b]">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <DemoBookingModal open={isDemoOpen} onOpenChange={setIsDemoOpen} />
    </section>
  )
}
